import { useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import {
  JobCatalogEnumsJobCatalogTreeNodeTypesEnum,
  TreeNodePageDocument,
} from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import {
  CategoryType,
  DEFAULT_CATEGORIES,
  NODE_TYPES,
  NodeType,
  SalaryRangeValue,
  TreeNode,
  findNodeByUuid,
  formatUuidToNodeType,
  getChildNodeType,
  getNodeId,
  getNodeType,
  isNodeType,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'

interface UseTreeNode {
  nodeId: number
  nodeName?: string
  nodeType: string
  currentAttributes: NodeAttribute[]
  currentNode?: TreeNode | null
  salaryRangeValue: SalaryRangeValue
  childrenInformation: ChildInformation[]
  activeTab: CategoryType
  setActiveTab: (type: CategoryType) => void
}

interface NodeAttribute {
  type: string
  takesPrecedence: boolean
  serializedValue: string
  id: string
  definedAt: NodeType
  inheritanceType: string
  attributeId?: string | null
}

interface ChildInformation {
  id: number
  name: string
  type: JobCatalogEnumsJobCatalogTreeNodeTypesEnum
  attributesJson: string
}

export const useTreeNode = (id: string | undefined): UseTreeNode => {
  const [activeTab, setActiveTab] = useState<CategoryType>(DEFAULT_CATEGORIES.OVERVIEW)
  const [currentAttributes, setCurrentAttributes] = useState<NodeAttribute[]>([])

  const nodeId = getNodeId(id)
  const nodeType = getNodeType(id)
  const isValidNode = nodeId && nodeType && !isNaN(nodeId)
  const childrenType = getChildNodeType(nodeType) || ''

  const { data } = useQuery(TreeNodePageDocument, {
    variables: {
      childType: formatUuidToNodeType(childrenType),
      nodeId: nodeId,
      nodeType: formatUuidToNodeType(nodeType) || '',
    },
    skip: !isValidNode,
  })

  const nodeAttributes = useMemo(
    () => data?.jobCatalog?.nodeAttributesConnection?.nodes || [],
    [data?.jobCatalog?.nodeAttributesConnection?.nodes]
  )

  const childrenInformation: ChildInformation[] = useMemo(() => {
    const rawChildren = data?.jobCatalog?.treeNodesConnection?.nodes || []

    return rawChildren.map((child) => ({
      id: child.id,
      name: child.name || '',
      type: child.type || '',
      attributesJson: child.attributesJson || '',
    }))
  }, [data?.jobCatalog?.treeNodesConnection?.nodes])

  const treeArray: TreeNode[] = useMemo(() => {
    try {
      const tree = data?.jobCatalog?.treesConnection?.nodes?.[0]
      if (!tree?.serializedTree) return []

      const parsed = JSON.parse(tree.serializedTree)
      return Array.isArray(parsed) ? parsed : [parsed]
    } catch (e) {
      return []
    }
  }, [data?.jobCatalog?.treesConnection?.nodes])

  const attachParents = (node: TreeNode, parent?: TreeNode): void => {
    node.parent = parent
    node.children?.forEach((child) => attachParents(child, node))
  }
  treeArray.forEach((root) => attachParents(root))

  const currentNode = useMemo(() => {
    if (!id || !treeArray.length) return undefined
    return findNodeByUuid(treeArray, id)
  }, [treeArray, id])

  const nodeName = currentNode?.name

  useUnsafeEffect(() => {
    if (normalizeNodeType(currentNode?.type) === NODE_TYPES.LEVEL) {
      setActiveTab(DEFAULT_CATEGORIES.COMPETENCIES)
    }
  }, [currentNode?.type])

  useUnsafeEffect(() => {
    const attributes = nodeAttributes
      .filter((node) => node.takesPrecedence)
      .map((attr) => ({
        ...attr,
        id: String(attr.id),
        definedAt: isNodeType(attr.definedAt) ? attr.definedAt : NODE_TYPES.ROOT,
      }))
    setCurrentAttributes(attributes)
  }, [activeTab, nodeAttributes])

  const salaryRange = nodeAttributes.find((a) => a.type === 'salary_range' && a.takesPrecedence)
  const salaryRangeValue: SalaryRangeValue = JSON.parse(salaryRange?.serializedValue || '{}')

  return {
    nodeId,
    nodeName,
    nodeType,
    currentNode,
    currentAttributes,
    childrenInformation,
    salaryRangeValue,
    activeTab,
    setActiveTab,
  }
}
